/**
 * Pure diff parsing utilities for DiffViewer.
 */

export interface DiffLine {
  type: 'add' | 'delete' | 'context' | 'header';
  content: string;
  oldLineNum?: number;
  newLineNum?: number;
}

export interface DiffChunk {
  header: string;
  lines: DiffLine[];
}

export interface ParsedDiff {
  fileName: string;
  additions: number;
  deletions: number;
  chunks: DiffChunk[];
}

export function parseDiff(diffText: string): ParsedDiff[] {
  if (!diffText) return [];

  const files: ParsedDiff[] = [];
  // Handle both LF and CRLF line endings per the unified diff spec
  const lines = diffText.split(/\r?\n/);

  let currentFile: ParsedDiff | null = null;
  let currentChunk: DiffChunk | null = null;
  let oldLineNum = 0;
  let newLineNum = 0;

  const flushChunk = () => {
    if (currentFile && currentChunk) {
      currentFile.chunks.push(currentChunk);
      currentChunk = null;
    }
  };

  const flushFile = () => {
    flushChunk();
    if (currentFile) {
      files.push(currentFile);
      currentFile = null;
    }
  };

  for (const rawLine of lines) {
    // Strip trailing \r left over from CRLF splitting on \n alone
    const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;

    // File header — starts a new file entry
    if (line.startsWith('diff --git ')) {
      flushFile();
      // Extract path after " b/" — handles paths that don't contain " b/"
      const bIdx = line.lastIndexOf(' b/');
      currentFile = {
        fileName: bIdx !== -1 ? line.slice(bIdx + 3) : 'unknown',
        additions: 0,
        deletions: 0,
        chunks: [],
      };
    }
    // Chunk header — @@ -L[,N] +L[,N] @@ [optional function context]
    else if (line.startsWith('@@ ')) {
      flushChunk();
      currentChunk = { header: line, lines: [] };

      // Spec: count is omitted when 1 (e.g. @@ -1 +1 @@ and @@ -0,0 +1,16 @@)
      const match = line.match(/^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/);
      if (match) {
        oldLineNum = parseInt(match[1], 10);
        newLineNum = parseInt(match[2], 10);
      }
    }
    // "No newline at end of file" indicator — part of the spec, skip silently
    else if (line.startsWith('\\ ')) {
      // intentionally ignored
    }
    // Diff content lines — only inside an active chunk
    else if (currentChunk) {
      if (line.startsWith('+')) {
        currentChunk.lines.push({
          type: 'add',
          content: line.slice(1),
          newLineNum: newLineNum++,
        });
        if (currentFile) currentFile.additions++;
      } else if (line.startsWith('-')) {
        currentChunk.lines.push({
          type: 'delete',
          content: line.slice(1),
          oldLineNum: oldLineNum++,
        });
        if (currentFile) currentFile.deletions++;
      } else if (line.startsWith(' ')) {
        currentChunk.lines.push({
          type: 'context',
          content: line.slice(1),
          oldLineNum: oldLineNum++,
          newLineNum: newLineNum++,
        });
      }
      // All other lines inside a chunk (e.g. extended headers that land here) are skipped
    }
  }

  flushFile();
  return files;
}

export function isMarkdownFile(fileName: string): boolean {
  return fileName.endsWith('.md');
}
